import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dev-nexus-play/artifacts/q14fzpwq_um_software_logo_white.png";

const links = [
  { id: "home", label: "Home", testid: NAV.linkHome },
  { id: "services", label: "Services", testid: NAV.linkServices },
  { id: "portfolio", label: "Portfolio", testid: NAV.linkPortfolio },
  { id: "about", label: "About", testid: NAV.linkAbout },
  { id: "contact", label: "Contact", testid: NAV.linkContact },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!onLanding) return;
    const sections = links.map((l) => document.getElementById(l.id)).filter(Boolean);
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [onLanding]);

  const goTo = (id) => {
    setOpen(false);
    if (onLanding) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <header
      data-testid={NAV.container}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || !onLanding ? "glass border-b border-cyan-900/40" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 h-20 flex items-center justify-between">
        <button
          data-testid={NAV.logo}
          onClick={() => (onLanding ? goTo("home") : navigate("/"))}
          className="flex items-center gap-3 group"
        >
          <span className="w-12 h-12 flex items-center justify-center border border-cyan-500/60 group-hover:shadow-neon-cyan group-hover:border-cyan-400 transition-all duration-200 bg-[#0a0a0a] p-0.5">
            <img
              src={LOGO_URL}
              alt="UM Software"
              className="w-full h-full object-contain"
              loading="eager"
            />
          </span>
          <span className="font-mono-display text-base tracking-widest text-slate-100">
            UM<span className="text-cyan-400">/</span>SOFTWARE
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => goTo(l.id)}
              className={`nav-link ${onLanding && active === l.id ? "active" : ""}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:block">
          <button
            data-testid={NAV.ctaContact}
            onClick={() => goTo("contact")}
            className="btn-neon"
          >
            Start_Mission
          </button>
        </div>

        <button
          data-testid={NAV.mobileToggle}
          className="md:hidden text-cyan-400 p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden glass border-t border-cyan-900/40">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => goTo(l.id)}
                data-testid={`${l.testid}-mobile`}
                className={`nav-link text-left ${onLanding && active === l.id ? "active" : ""}`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => goTo("contact")}
              className="btn-neon self-start"
              data-testid={`${NAV.ctaContact}-mobile`}
            >
              Start_Mission
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
